import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Button, FlexBox, Text } from '@eltoro-ui/components';
import { SelectOptionType } from 'Pages/ProspectActivity/components/FindProspectFilters/PriceFilter';
import { formatSQFT } from 'Pages/Orderlines/helpers';
import './FindYourProspectFilters.scss';

export const SquareFootFilter = ({
  modalOpen,
  setIsModalOpen,
  setSquareFeet,
  currentFilters,
  postedFilters,
  setPostedFilter,
  setIsClicked,
  isClicked,
}: {
  modalOpen: any;
  setIsModalOpen: any;
  setSquareFeet: any;
  currentFilters: any;
  postedFilters: any;
  setPostedFilter: any;
  setIsClicked?: any;
  isClicked?: any;
}) => {
  const dropdownRef = useRef<Dropdown | null>(null);
  const [selectedRange, setSelectedRange] = useState('');
  const [customMin, setCustomMin] = useState('');
  const [customMax, setCustomMax] = useState('');
  const [customRange, setCustomRange] = useState(currentFilters.squareFoot);
  const [error, setError] = useState('');
  const [isOptionSelected, setIsOptionSelected] = useState('');
  const [isDisabled, setIsDisabled] = useState(true);

  const arrOfSQFT: SelectOptionType[] = [
    { label: 'Any square-foot', value: 'Any' },
    { label: '0 - 999 Sqft', value: '0 - 999' },
    { label: '1,000 Sqft - 1,499 Sqft', value: '1000 - 1499' },
    { label: '1,500 Sqft - 1999 Sqft', value: '1500 - 1999' },
    { label: '2,000 Sqft - 2,499 Sqft', value: '2000 - 2499' },
    { label: '2,500 Sqft - 2,999 Sqft', value: '2500 - 2999' },
    { label: '3,000 Sqft - 3,499 Sqft', value: '3000 - 3499' },
    { label: '3,500 Sqft - 3,999 Sqft', value: '3500 - 3999' },
    { label: '4,000 Sqft - 4,900 Sqft', value: '4000 - 4900' },
    { label: 'Custom square-foot', value: 'Custom square-foot' },
  ];
  useEffect(() => {
    if (currentFilters.squareFoot) {
      const findSQFTInSQFTList = arrOfSQFT.find(opt => opt.value === currentFilters.squareFoot);
      if (!findSQFTInSQFTList) {
        const customRanges = currentFilters.squareFoot?.split('-');
        setCustomMin(customRanges?.[0] ?? '');
        setCustomMax(customRanges?.[1] ?? '');
        setIsOptionSelected('Custom square-foot');
      } else {
        setIsOptionSelected(currentFilters.squareFoot);
      }
    }
  }, []);

  const cancelDefaultActions = (
    e: React.MouseEvent<Element> | React.ChangeEvent<Element> | DropdownChangeEvent
  ) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleCustomInputChange = (value: string, type: 'min' | 'max') => {
    let numericValue = value.replace(/\D/g, '');
    if (numericValue !== '0') {
      numericValue = numericValue.replace(/^0+/, '');
    }
    if (type === 'min') {
      setCustomMin(numericValue);
    } else {
      setCustomMax(numericValue);
    }
  };

  const validateCustomRange = (min: string, max: string) => {
    const minNum = parseInt(min || '0', 10);
    const maxNum = parseInt(max || '0', 10);
    const trimmedMin = min.trim();
    if (!trimmedMin && maxNum) {
      setCustomMin('0');
    }
    if (!trimmedMin && !max.trim()) {
      setError('Both fields are required');
    } else if (maxNum === 0) {
      setError('Max should be greater than zero.');
    } else if (min && max && minNum > maxNum) {
      setError('Min should be lower than Max.');
    } else {
      setError('');
      const range = `${minNum} - ${maxNum}`;
      setSelectedRange(range);
      setCustomRange(range);
      if (dropdownRef.current) {
        dropdownRef.current.hide();
      }
    }
  };

  const onChange = (e: DropdownChangeEvent | React.MouseEvent, value: string) => {
    if (value === 'Custom square-foot') {
      cancelDefaultActions(e);
    }
    setIsOptionSelected(value);
    setSelectedRange(value);
  };

  const onClickCancelOrClear = (type: string) => {
    setIsModalOpen({ ...modalOpen, squareFoot: false });
    if (type === 'Clear') {
      setPostedFilter({
        ...postedFilters,
        squareFoot: null,
      });
      setSquareFeet({
        ...currentFilters,
        squareFoot: null,
      });

      if (selectedRange || currentFilters?.squareFoot) {
        setIsClicked({ ...isClicked, isLoaded: false });
      }
    }
    // show prospects box on cancel
    // else {
    //   setIsClicked({ ...isClicked, isLoaded: true })
    // }
  };

  const onSaveChanges = () => {
    const isCustomSelected = selectedRange === 'Custom square-foot';
    const selectedSQFT = isCustomSelected ? customRange : selectedRange;

    const finalSQFT =
      selectedSQFT || // user’s new choice
      currentFilters?.squareFoot || // previously applied filter
      null;

    setIsModalOpen({ ...modalOpen, squareFoot: false });
    setIsClicked({ ...isClicked, isLoaded: false });

    setPostedFilter({
      ...postedFilters,
      squareFoot: finalSQFT,
    });

    setSquareFeet({
      ...currentFilters,
      squareFoot: finalSQFT,
    });
  };

  useEffect(() => {
    if (selectedRange || currentFilters?.squareFoot) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [selectedRange, currentFilters]);

  const itemTemplate = (option: SelectOptionType) => {
    const isCustomRange = option.label === 'Custom square-foot';
    const handleOptionClick = (e: React.MouseEvent) => {
      if (option.label === 'Custom square-foot') {
        cancelDefaultActions(e);
      } else {
        const findSQFRInSQFTList = arrOfSQFT.find(opt => opt.value === currentFilters.squareFoot);
        if (findSQFRInSQFTList) {
          setCustomMin('');
          setCustomMax('');
          setCustomRange('');
          setError('');
        } else {
          const customRanges = currentFilters.squareFoot?.split('-');
          setCustomMin(customRanges?.[0] ?? '');
          setCustomMax(customRanges?.[1] ?? '');
          setCustomRange(currentFilters.squareFoot);
          setError('');
        }
      }
      onChange(e, option.value);
    };
    const handleApply = () => {
      validateCustomRange(customMin, customMax);
    };

    return (
      <button
        className={classnames('selection-wrapper', {
          'custom-select':
            isOptionSelected === 'Custom square-foot' && option.value === 'Custom square-foot',
        })}
        onClick={handleOptionClick}
      >
        <FlexBox UNSAFE_className="option-label" alignItems="center" gap="8px">
          <button
            className={classnames('select-range-radio-btn', {
              checked: isOptionSelected === option.value,
            })}
          >
            {isOptionSelected === option.value && <div className="select-range-radio-btn-inner" />}
          </button>
          <label htmlFor={option.label}>{option.label}</label>
        </FlexBox>
        {isOptionSelected === 'Custom square-foot' && isCustomRange && (
          <FlexBox
            flexDirection="column"
            gap="12px"
            alignItems="flex-end"
            UNSAFE_className="custom-ranges"
          >
            <FlexBox
              UNSAFE_className="custom-input-wrapper"
              justifyContent="space-between"
              gap="8px"
              alignSelf="center"
            >
              <FlexBox flexDirection="column" alignItems="flex-start">
                Min
                <div className="input-wrapper">
                  <input
                    name="minRange"
                    className={classnames('min-max-input sqft-input', { 'err-input': error })}
                    value={customMin ? `${customMin}` : ''}
                    onChange={e => {
                      cancelDefaultActions(e);
                      handleCustomInputChange(e.target.value, 'min');
                    }}
                    onClick={e => {
                      cancelDefaultActions(e);
                    }}
                    onKeyDown={e => e.stopPropagation()}
                  />
                </div>
              </FlexBox>
              <FlexBox flexDirection="column" alignItems="flex-start">
                Max
                <div className="input-wrapper">
                  <input
                    name="maxRange"
                    className={classnames('min-max-input sqft-input', { 'err-input': error })}
                    value={customMax ? `${customMax}` : ''}
                    onChange={e => {
                      cancelDefaultActions(e);
                      handleCustomInputChange(e.target.value, 'max');
                    }}
                    onClick={e => {
                      cancelDefaultActions(e);
                    }}
                    onKeyDown={e => e.stopPropagation()}
                  />
                </div>
              </FlexBox>
            </FlexBox>
            {error && <p className="err-msg">{error}</p>}
            <div className="divider" />
            <Button kind="link" weight="400" onClick={handleApply}>
              Apply
            </Button>
          </FlexBox>
        )}
      </button>
    );
  };

  return (
    <div className="FindYourProspectFilters__priceFilterConatiner">
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Text size="m" tag="div" on="grey-500" UNSAFE_className="title">
          Square Foot
        </Text>
        <div className="title-divider" />
      </div>
      <br />
      <div className="FindYourProspectFilters__priceFilter" style={{ marginBottom: '20px' }}>
        <Dropdown
          ref={dropdownRef}
          value={isOptionSelected || selectedRange}
          onChange={e => {
            if (e.target.value === 'Custom square-foot') {
              cancelDefaultActions(e);
            }
            onChange(e, e.target.value);
          }}
          valueTemplate={(option, props) => {
            if (!option) return props.placeholder;
            if (option.value === 'Custom square-foot') {
              const findSQFTInSQFTList = arrOfSQFT.find(opt => opt.value === customRange);
              const value =
                !findSQFTInSQFTList && (customMax || customMin)
                  ? formatSQFT(customRange)
                  : option.value;
              return value ?? props.placeholder;
            }
            return option.value === 'Any' ? option.label : formatSQFT(option.value);
          }}
          options={arrOfSQFT}
          optionLabel="label"
          placeholder="Select square-foot"
          itemTemplate={itemTemplate}
          panelClassName="range-select-panel"
          className="range-select"
        />
      </div>
      <hr />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          kind="default"
          size="l"
          UNSAFE_className="SellYourListingFaster__modalCancel"
          weight="bold"
          onClick={() => onClickCancelOrClear('Cancel')}
        >
          Cancel
        </Button>
        <Button
          kind="primary"
          UNSAFE_className="SellYourListingFaster__modalSave"
          size="l"
          weight="bold"
          onClick={onSaveChanges}
          disabled={isDisabled}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};
